import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Download, Search, RotateCcw, Loader2, PackageSearch } from 'lucide-react';

const ALL = 'all';
const gbp = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid={testId}>
        <SelectValue placeholder="Any" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const InstallationsReport = () => {
  const [opts, setOpts] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [f, setF] = useState({
    start_date: '', end_date: '',
    installation_type: ALL,
    panel_manufacturer: ALL, inverter_manufacturer: ALL, battery_manufacturer: ALL,
    ev_charger_manufacturer: ALL,
  });

  useEffect(() => {
    api.get('/reports/installation-filters').then((r) => setOpts(r.data)).catch(() => setOpts({}));
  }, []);

  const buildParams = () => {
    const p = {};
    if (f.start_date) p.start_date = f.start_date;
    if (f.end_date) p.end_date = f.end_date;
    ['installation_type', 'panel_manufacturer', 'inverter_manufacturer', 'battery_manufacturer', 'ev_charger_manufacturer'].forEach((k) => {
      if (f[k] && f[k] !== ALL) p[k] = f[k];
    });
    return p;
  };

  const runReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/installations', { params: buildParams() });
      setData(data);
    } catch {
      toast.error('Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); /* initial load */ }, []); // eslint-disable-line

  const reset = () => {
    setF({
      start_date: '', end_date: '',
      installation_type: ALL, panel_manufacturer: ALL,
      inverter_manufacturer: ALL, battery_manufacturer: ALL, ev_charger_manufacturer: ALL,
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await api.get('/reports/installations/export/csv', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `installations_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4" data-testid="installations-report">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Installations by contract-signed date</CardTitle>
          <CardDescription>Filter signed jobs by date range and equipment make/model</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Signed from</Label>
              <Input type="date" value={f.start_date} onChange={(e) => set('start_date')(e.target.value)}
                className="h-9" data-testid="inst-start-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Signed to</Label>
              <Input type="date" value={f.end_date} onChange={(e) => set('end_date')(e.target.value)}
                className="h-9" data-testid="inst-end-date" />
            </div>
            <FilterSelect label="Installation type" value={f.installation_type} onChange={set('installation_type')}
              options={opts?.installation_types || []} testId="inst-type-select" />
            <FilterSelect label="Panel make" value={f.panel_manufacturer} onChange={set('panel_manufacturer')}
              options={opts?.panel_manufacturers || []} testId="inst-panel-select" />
            <FilterSelect label="Inverter make" value={f.inverter_manufacturer} onChange={set('inverter_manufacturer')}
              options={opts?.inverter_manufacturers || []} testId="inst-inverter-select" />
            <FilterSelect label="Battery make" value={f.battery_manufacturer} onChange={set('battery_manufacturer')}
              options={opts?.battery_manufacturers || []} testId="inst-battery-select" />
            <FilterSelect label="EV charger make" value={f.ev_charger_manufacturer} onChange={set('ev_charger_manufacturer')}
              options={opts?.ev_charger_manufacturers || []} testId="inst-ev-select" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={runReport} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="inst-run-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Run report
            </Button>
            <Button variant="outline" onClick={reset} data-testid="inst-reset-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={exporting || !data?.total} data-testid="inst-export-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inst-summary">
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Installations</p>
            <p className="text-2xl font-bold text-slate-900">{data.total}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Total value</p>
            <p className="text-2xl font-bold text-slate-900">{gbp(data.total_value)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Total capacity</p>
            <p className="text-2xl font-bold text-slate-900">{(data.total_size || 0).toFixed(1)} kW</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">By type</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(data.by_type || {}).map(([t, n]) => (
                <Badge key={t} variant="secondary" className="text-xs">{t}: {n}</Badge>
              ))}
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Results table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          ) : (data?.data?.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Signed</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Panels</TableHead>
                    <TableHead>Inverter</TableHead>
                    <TableHead>Battery</TableHead>
                    <TableHead>EV charger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((r) => (
                    <TableRow key={r.id} data-testid={`inst-row-${r.id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-800">{r.name}</p>
                        <p className="text-xs text-slate-400">{r.postcode}</p>
                      </TableCell>
                      <TableCell className="text-sm">{(r.contract_signed_date || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{r.installation_type || '—'}</TableCell>
                      <TableCell className="text-sm">{r.system_size ? `${r.system_size} kW` : '—'}</TableCell>
                      <TableCell className="text-sm">{gbp(r.system_cost)}</TableCell>
                      <TableCell className="text-sm">{r.panel || '—'}</TableCell>
                      <TableCell className="text-sm">{r.inverter || '—'}</TableCell>
                      <TableCell className="text-sm">{r.battery || '—'}</TableCell>
                      <TableCell className="text-sm">{r.ev_charger || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 text-slate-400" data-testid="inst-empty">
              <PackageSearch className="w-8 h-8 mb-2" />
              <p className="text-sm">No signed installations match these filters.</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default InstallationsReport;
